import React, { useState } from 'react';

import { Modal, Button } from '@common/index';
import { Checkbox, CircularProgress, FormControlLabel, FormGroup, Stack } from '@mui/material';

import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { flowActions } from '@store/slices';
import { AppDispatch, State } from '@store/index';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getDesyncedBuckets, syncBuckets } from '@requests/buckets/buckets.requests';
import { Sync } from '@mui/icons-material';

export const SyncBucket: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();

  const { enqueueSnackbar } = useSnackbar();
  const { service_id } = useParams();

  const username = useSelector((state: State) => state.auth.username);
  const open = useSelector((state: State) => state.flow.syncBuckets);
  const setOpen = (val: boolean) => dispatch(flowActions.setSyncBuckets(val));

  const [checked, setChecked] = useState<string[]>([]);

  const { data: desynced = [] } = useQuery({
    queryKey: ['globalFiles', 'all_buckets', { service_id: service_id as string }],
    queryFn: getDesyncedBuckets,
    enabled: location.pathname.includes('buckets'),
  });

  const { mutateAsync: sync, status: syncStatus } = useMutation({
    mutationFn: syncBuckets,
    onSuccess: (data) => {
      if (!data) return;

      queryClient.invalidateQueries({ queryKey: ['globalFiles'] });

      if (data?.error) {
        enqueueSnackbar(data?.error?.error || data?.error, {
          variant: 'error',
          autoHideDuration: 10000,
        });

        enqueueSnackbar('Failed to sync buckets!', {
          variant: 'error',
          autoHideDuration: 10000,
        });

        return;
      }

      enqueueSnackbar('Buckets synced successfully!', { variant: 'success', autoHideDuration: 3000 });
      setChecked([]);
      setOpen(false);
    },
  });

  const isSyncLoading = syncStatus === 'pending';

  const create = () => {
    enqueueSnackbar(checked.length > 1 ? 'Syncing buckets...' : 'Syncing bucket...', {
      variant: 'info',
      autoHideDuration: 3000,
    });

    sync({
      user_id: username as string,
      service_id: service_id as string,
      buckets: checked,
    });
  };

  const addBucketToSync = (e: React.ChangeEvent<HTMLInputElement>, bucket_name: string) => {
    const checked = e.target.checked;

    if (!checked) setChecked((prev) => prev.filter((item) => item !== bucket_name));
    else setChecked((prev) => prev.concat(bucket_name));
  };

  return (
    <Modal
      open={Boolean(open)}
      background='primary'
      setOpen={setOpen}
      header='Synchronise Buckets'
      icon={<Sync />}
      info='If a bucket was created directly through the portal or Telnyx API, the bucket will not show in your Flow account. You can still add this bucket to your account by selecting it from the list below.'
      body={
        <FormGroup>
          {desynced?.map((bucket: { name: string; date_created: string }) => (
            <FormControlLabel
              key={bucket.name}
              control={
                <Checkbox
                  checked={checked.findIndex((item) => item === bucket.name) !== -1}
                  onChange={(e) => addBucketToSync(e, bucket.name)}
                />
              }
              label={bucket.name}
            />
          ))}
        </FormGroup>
      }
      actions={
        <Stack direction='row' spacing={1}>
          <Button
            small
            onClick={() => {
              setOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            small
            customColor='primary'
            onClick={create}
            disabled={!checked.length}
            sx={{ minWidth: '65px !important' }}
          >
            {isSyncLoading ? <CircularProgress sx={{ color: 'white' }} size={14} thickness={4} /> : 'Sync'}
          </Button>
        </Stack>
      }
    />
  );
};
